import * as React from 'react';

import { Checkbox, Col } from '@rematter/ui-web';
import { useToast } from '@rematter/ui-web/hooks';
import { useAtomValue, useSetAtom } from 'jotai';

import * as govReportingState from '../../_state/govReporting.state';
import { useTranslations } from '../../i18n/useTranslations';

const FIELD_GROUPS = {
  scaleTicket: [
    'ticketNumber',
    'ticketDate',
    'grossWeight',
    'tareWeight',
    'netWeight',
  ],
  publicSupplier: [
    'supplierName',
    'supplierAddress',
    'supplierPhone',
    'supplierIdType',
    'supplierId',
  ],
  vehicle: [
    'vehicleMake',
    'vehicleModel',
    'vehicleYear',
    'vehiclePlate',
    'vehicleVin',
  ],
  material: [
    'materialDescription',
    'materialCode',
    'materialQuantity',
    'materialUnit',
  ],
} as const;

type FieldGroupKey = keyof typeof FIELD_GROUPS;

export const FieldSelectionPanel = () => {
  const t = useTranslations();
  const configQuery = useAtomValue(
    govReportingState.$currentGovReportingConfig,
  );
  const updateConfig = useSetAtom(govReportingState.$updateGovReportingConfig);
  const toast = useToast();
  const config = configQuery.data;

  const fields = React.useMemo(
    () => (config?.fields ?? {}) as Record<string, Record<string, boolean>>,
    [config?.fields],
  );

  const handleFieldChange = async (
    group: FieldGroupKey,
    field: string,
    checked: boolean,
  ) => {
    if (!config) return;
    const updatedFields: Record<string, Record<string, boolean>> = {
      ...fields,
      [group]: {
        ...(fields[group] ?? {}),
        [field]: checked,
      },
    };
    try {
      await updateConfig({ id: config.id, fields: updatedFields });
    } catch (error) {
      toast.error(
        t.complianceView.govReporting.dialog.messages.updateError(
          error instanceof Error ? error.message : 'Unknown error',
        ),
      );
    }
  };

  const fieldLabels =
    t.complianceView.govReporting.detail.fieldSelection.fields;
  const groupLabels =
    t.complianceView.govReporting.detail.fieldSelection.groups;

  return (
    <Col className="@FieldSelectionPanel gap-4 rounded-lg border border-neutral/10 p-5">
      <div className="text-sm font-semibold">
        {t.complianceView.govReporting.detail.sections.fieldSelection}
      </div>
      <div className="grid grid-cols-2 gap-6">
        {(Object.keys(FIELD_GROUPS) as FieldGroupKey[]).map((group) => (
          <Col key={group} gap3>
            <div className="text-xs font-medium tracking-wide text-neutral/50 uppercase">
              {groupLabels[group]}
            </div>
            <Col gap2>
              {FIELD_GROUPS[group].map((field) => (
                <Checkbox
                  key={field}
                  isSelected={fields[group]?.[field] ?? false}
                  onChange={(checked) =>
                    handleFieldChange(group, field, checked)
                  }
                >
                  <span className="text-sm">
                    {fieldLabels[field as keyof typeof fieldLabels] ?? field}
                  </span>
                </Checkbox>
              ))}
            </Col>
          </Col>
        ))}
      </div>
    </Col>
  );
};
